import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import {
  Table, Row, Col, Modal, Button, Space, message, Alert, Spin, Typography
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { PlusOutlined } from '@ant-design/icons';
import * as api from 'app/shared/api';
import { Centre, School } from 'app/shared/model';
import ScopeFilterRow from 'app/shared/components/ScopeFilterRow';
import {
  buildAssignedCentresSchoolsList,
  buildCentreFilterOptionsFromFees,
  buildSchoolFilterOptionsFromFees,
  centreKeysMatch,
  filterCancellationFeesByScope,
  getActiveFeeAssignmentSummary,
  hasActiveScopeFilter,
  schoolKeysMatch,
  type CancellationFeeScopeFilter,
} from '../domain/cancellationFeeScopeFilter';
import {
  changeSelectedProductType,
  filterData,
  getCancellationFeeProductById,
  listCancellationFeesProductsData,
  resetCancellationFeeModal,
  setFormErrors
} from '../actions';
import { getCancellationFeesData, getCancellationFeesProductsData } from '../selectors';
import { CancellationFee } from 'app/shared/model/CancellationFee';
import DateHelper from 'app/helpers/dateHelper';
import { getSelectedDivisionId } from 'app/shared/division/selectedDivisionStorage';
import TableSearch from 'app/shared/components/Table/TableSearch';
import TableActionButtons from 'app/shared/components/Table/TableActionButtons';
import FilterResetLink from 'app/shared/components/Table/FilterResetLink';
import TablePagination from 'app/shared/components/Table/TablePagination';
import AddEditCancellationFeeForm from 'app/pages/Products/components/AddEditCancellationFeeForm';
import CancellationFeeSaveProgress, {
  CancellationFeeSaveStage,
} from 'app/pages/Products/components/CancellationFeeSaveProgress';
import { useSignalREvent, PublishedEventType, StatusIcon } from 'app/helpers/signalRHelper';
import './CancellationFees.css';
import { sortProductsWithStatus, useProductGridSorting } from './productGridSorting';
import ProductStatusFilter from './ProductStatusFilter';
import { applyProductStatusFilter, ProductStatusFilterValue } from './productStatusFilterUtils';

const mapApiToGrid = (dto: any): CancellationFee => {
  return {
    id: dto.productId ?? dto.id ?? 0,
    name: dto.productName ?? dto.name ?? '',
    divisionId: dto.divisionId ?? 0,
    accountCategoryId: dto.accountCategoryId ?? 0,
    accountCategoryText: dto.accountCategoryText ?? '',
    productCategoryId: dto.productCategoryId ?? 0,
    productCategoryText: dto.productCategoryText ?? '',
    isPublished: dto.active ?? false,
    active: dto.active ?? false,
    lastModifiedDate: dto.lastModifiedDate ?? null,
    lastUpdatedBy: dto.lastModifiedBy ?? dto.lastUpdatedBy ?? '',
    entityUpdate: dto.entityUpdate,
    schools: Array.isArray(dto.schools)
      ? dto.schools.map((s: any) => ({
          schoolId: s.schoolId ?? s.id ?? 0,
          schoolName: s.schoolName ?? s.name ?? '',
          centreId: s.centreId ?? 0,
          centreName: s.centreName ?? s.centreName ?? '',
        }))
      : [],
    schoolIds: Array.isArray(dto.schoolIds) ? dto.schoolIds : [],
    centreIds: Array.isArray(dto.centreIds) ? dto.centreIds : [],
  };
};

export interface CancellationFeesGridProps {}

interface StateProps {
  filteredData?: CancellationFee[];
  cancellationFeeProducts?: CancellationFee[];
}

interface DispatchProps {
  onCancel: () => Promise<CancellationFee[]>;
  onFilterData: (filteredData: CancellationFee[] | undefined) => void;
  onSelectedData: (item: CancellationFee | undefined | void) => void;
  onLoadCancellationFees: (divisionId: number) => Promise<void>;
  onListCancellationFees: (items: CancellationFee[]) => void;
  onSelectCancellationFeesTab: () => void;
}

type InternalProps = StateProps & DispatchProps & CancellationFeesGridProps;

function CancellationFeesGrid(props: InternalProps) {
  const [showFilters, setShowFilters] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState<ProductStatusFilterValue>('all');
  const [isSearchActive, setIsSearchActive] = React.useState(false);
  const [searchFilteredData, setSearchFilteredData] = React.useState<CancellationFee[] | undefined>(undefined);
  const [scopeFilter, setScopeFilter] = React.useState<CancellationFeeScopeFilter>({
    specificSchool: false,
    centreId: null,
    schoolId: null,
  });
  const [centres, setCentres] = React.useState<Centre[]>([]);
  const [schools, setSchools] = React.useState<School[]>([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);
  const { sortKey, sortOrder, getColumnSortProps, handleSorterChange } = useProductGridSorting();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalId, setModalId] = React.useState<number | null>(null);
  const [saveStage, setSaveStage] = React.useState<CancellationFeeSaveStage>('idle');
  const isFormSaving = saveStage !== 'idle';
  const [schoolListModalVisible, setSchoolListModalVisible] = React.useState(false);
  const [schoolListDetails, setSchoolListDetails] = React.useState<string[]>([]);
  const [divisionId, setDivisionId] = React.useState<number>(getSelectedDivisionId());
  const lastDivisionRef = React.useRef(divisionId);

  React.useEffect(() => {
    props.onSelectCancellationFeesTab();
  }, [props.onSelectCancellationFeesTab]);

  React.useEffect(() => {
    const onDivisionChanged = () => setDivisionId(getSelectedDivisionId());
    window.addEventListener('selectedDivisionChanged', onDivisionChanged);
    return () => window.removeEventListener('selectedDivisionChanged', onDivisionChanged);
  }, []);

  React.useEffect(() => {
    if (lastDivisionRef.current === divisionId) {
      return;
    }
    lastDivisionRef.current = divisionId;
    props.onFilterData(undefined);
    setCurrentPage(1);
    setScopeFilter({
      specificSchool: false,
      centreId: null,
      schoolId: null,
    });
  }, [divisionId, props.onFilterData]);

  React.useEffect(() => {
    if (divisionId) {
      props.onLoadCancellationFees(divisionId);
    }
  }, [divisionId, props.onLoadCancellationFees]);

  React.useEffect(() => {
    let isMounted = true;
    Promise.all([api.getAllCentres(), api.getAllSchools()])
      .then(([loadedCentres, loadedSchools]) => {
        if (!isMounted) return;
        setCentres(loadedCentres ?? []);
        setSchools(loadedSchools ?? []);
      })
      .catch(() => {
        if (!isMounted) return;
        setCentres([]);
        setSchools([]);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const productList = props.cancellationFeeProducts ?? [];

  const applyAllFilters = React.useCallback(
    (
      source: CancellationFee[],
      overrides?: {
        showFiltersPanel?: boolean;
        searchActive?: boolean;
        searchData?: CancellationFee[];
        status?: ProductStatusFilterValue;
        scope?: CancellationFeeScopeFilter;
      }
    ) => {
      const showPanel = overrides?.showFiltersPanel ?? showFilters;
      const activeSearch = overrides?.searchActive ?? isSearchActive;
      const searchData = overrides?.searchData ?? searchFilteredData;
      const status = overrides?.status ?? statusFilter;
      const scope = overrides?.scope ?? scopeFilter;

      let result = source;

      if (showPanel && hasActiveScopeFilter(scope)) {
        result = filterCancellationFeesByScope(result, scope);
      } else if (!showPanel && activeSearch && searchData !== undefined) {
        result = searchData;
      }

      result = applyProductStatusFilter(result, status);

      const hasScopeFilter = showPanel && hasActiveScopeFilter(scope);
      const hasSearch = !showPanel && activeSearch;
      const hasStatusFilter = status !== 'all';

      if (!hasScopeFilter && !hasSearch && !hasStatusFilter) {
        return undefined;
      }

      return result;
    },
    [showFilters, statusFilter, scopeFilter, isSearchActive, searchFilteredData]
  );

  const centreFilterOptions = React.useMemo(
    () => buildCentreFilterOptionsFromFees(productList, centres),
    [productList, centres]
  );

  const schoolFilterOptions = React.useMemo(
    () => buildSchoolFilterOptionsFromFees(productList, schools, centres),
    [productList, schools, centres]
  );

  const centreFilterSelectValue = React.useMemo(() => {
    if (scopeFilter.centreId == null) return undefined;
    const match = centreFilterOptions.find((option) =>
      centreKeysMatch(option.value, scopeFilter.centreId!)
    );
    return match?.value;
  }, [centreFilterOptions, scopeFilter.centreId]);

  const schoolFilterSelectValue = React.useMemo(() => {
    if (scopeFilter.schoolId == null) return undefined;
    const match = schoolFilterOptions.find((option) =>
      schoolKeysMatch(option.value, scopeFilter.schoolId!)
    );
    return match?.value;
  }, [schoolFilterOptions, scopeFilter.schoolId]);

  const pushFilteredData = React.useCallback(
    (source: CancellationFee[]) => {
      props.onFilterData(applyAllFilters(source));
    },
    [applyAllFilters, props.onFilterData]
  );

  const resetScopeFilter = React.useCallback(() => {
    setScopeFilter({
      specificSchool: false,
      centreId: null,
      schoolId: null,
    });
  }, []);

  const updateScopeFilter = React.useCallback(
    (updater: (current: CancellationFeeScopeFilter) => CancellationFeeScopeFilter) => {
      setScopeFilter((current) => updater(current));
      setCurrentPage(1);
    },
    []
  );

  React.useEffect(() => {
    pushFilteredData(productList);
  }, [scopeFilter, statusFilter, showFilters, isSearchActive, searchFilteredData, productList, pushFilteredData]);

  const handleClearScopeFilter = () => {
    resetScopeFilter();
    setCurrentPage(1);
    pushFilteredData(productList);
  };

  const getItem = (id: number) => {
    const item = (props.cancellationFeeProducts ?? []).find(f => f.id === id);
    props.onSelectedData(item);
    setModalId(id);
    setModalOpen(true);
  };

  const openSchoolListModal = (record: CancellationFee) => {
    setSchoolListDetails(buildAssignedCentresSchoolsList(record, centres, schools));
    setSchoolListModalVisible(true);
  };

  const refreshGridEvent = async () => {
    const list = await api.getAllCancellationFees(divisionId);
    const mapped = Array.isArray(list) ? list.map(mapApiToGrid) : [];
    props.onListCancellationFees(mapped);
    props.onFilterData(applyAllFilters(mapped));
  };

  const { status, lastEventReceived } = useSignalREvent(
    [PublishedEventType.CancellationFeeEventPublished],
    refreshGridEvent,
    divisionId
  );

  const filteredData = props.filteredData ?? productList;
  const sortedData = React.useMemo(() => {
    return sortProductsWithStatus(filteredData as CancellationFee[], sortKey, sortOrder, statusFilter);
  }, [filteredData, sortKey, sortOrder, statusFilter]);
  const pagedData = TablePagination.dataPaging(sortedData as any[], currentPage, itemsPerPage);
  const totalItems = sortedData.length;
  const centresSchoolsColumnTitle = scopeFilter.specificSchool ? 'Schools' : 'Centres / Schools';
  const assignedCentresSchoolsModalTitle = scopeFilter.specificSchool
    ? 'Assigned Schools'
    : 'Assigned Centres / Schools';

  const columns = [
    {
      title: 'Id',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      render: (id: number) => id,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSortProps('name'),
      render: (text: string, record: CancellationFee) => (
        <a href="#0" onClick={e => { e.preventDefault(); getItem(record.id); }}>{text}</a>
      ),
    },
    {
      title: centresSchoolsColumnTitle,
      dataIndex: 'schools',
      key: 'schools',
      render: (_: any, record: CancellationFee) => {
        const { schoolNames, centreCount } = getActiveFeeAssignmentSummary(record, centres, schools);
        const summary = [];

        if (centreCount > 0) {
          summary.push(`${centreCount} centre${centreCount === 1 ? '' : 's'}`);
        }
        if (schoolNames.length > 0) {
          summary.push(`${schoolNames.length} school${schoolNames.length === 1 ? '' : 's'}`);
        }

        if (!summary.length) {
          return <span>-</span>;
        }

        const visibleNames = schoolNames.slice(0, 2);
        const remainingSchoolCount = schoolNames.length - visibleNames.length;
        const text = [
          ...visibleNames,
          ...(centreCount ? [`${centreCount} centre${centreCount === 1 ? '' : 's'}`] : []),
        ].join(', ');

        return (
          <span style={{ cursor: 'pointer' }} onClick={() => openSchoolListModal(record)}>
            {text}
            {remainingSchoolCount > 0 && (
              <a style={{ marginLeft: 8 }}>+{remainingSchoolCount} more</a>
            )}
          </span>
        );
      },
    },
    {
      title: 'Last Modified Date',
      dataIndex: 'lastModifiedDate',
      key: 'lastModifiedDate',
      width: 160,
      ...getColumnSortProps('lastModifiedDate'),
      render: (value: string | null) => DateHelper.ToDateString(value ?? undefined),
    },
    {
      title: 'Last Updated By',
      dataIndex: 'lastUpdatedBy',
      key: 'lastUpdatedBy',
      width: 150,
      ...getColumnSortProps('lastUpdatedBy'),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      width: 80,
      render: (val: boolean) =>
        val ? <span className="cancellation-fees-active-yes">Yes</span> : <span className="cancellation-fees-active-no">No</span>,
    },
  ];

  const handleSaved = React.useCallback(async () => {
    try {
      const latest = await props.onCancel();
      const latestList = Array.isArray(latest) ? latest : (props.cancellationFeeProducts ?? []);
      props.onFilterData(applyAllFilters(latestList));
    } catch (error) {
      console.error('Failed to refresh cancellation fees after save', error);
      message.warning('Cancellation fee saved, but we could not refresh the list. Please refresh the page.');
    } finally {
      setCurrentPage(1);
      setModalId(null);
      setModalOpen(false);
    }
  }, [applyAllFilters, props.cancellationFeeProducts, props.onCancel, props.onFilterData]);

  if (!divisionId) {
    return (
      <Alert
        type="warning"
        showIcon
        title="Division required"
        description="Please select a division from the header to view and manage cancellation fees."
      />
    );
  }

  return (
    <div>
      <Row gutter={16} className="cancellation-fees-row">
        <Col flex={2}>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <TableSearch
                disabled={showFilters}
                dataList={productList as unknown as any[]}
                onSearch={(data, searchActive = false) => {
                  setCurrentPage(1);
                  setIsSearchActive(searchActive);
                  setSearchFilteredData(data as unknown as CancellationFee[]);
                  props.onFilterData(applyAllFilters(productList, {
                    searchActive,
                    searchData: data as unknown as CancellationFee[],
                  }));
                }}
              />
            </div>
            <ProductStatusFilter
              value={statusFilter}
              onChange={(nextStatus) => {
                setStatusFilter(nextStatus);
                setCurrentPage(1);
                props.onFilterData(applyAllFilters(productList, { status: nextStatus }));
              }}
            />
          </div>
        </Col>
        <Col flex={3} className="cancellation-fees-col-right">
          <Space>
            <StatusIcon
              status={status}
              lastEventReceived={lastEventReceived}
              unpublishedChanges={Array.isArray(filteredData) && filteredData.some(x => x?.entityUpdate === true)}
              onManualRefresh={refreshGridEvent}
            />
            <TableActionButtons
              showFilters={showFilters}
              dataList={filteredData}
              onShowFiltersClick={() => {
                const nextShowFilters = !showFilters;
                setShowFilters(nextShowFilters);
                if (nextShowFilters) {
                  resetScopeFilter();
                }
                setCurrentPage(1);
                pushFilteredData(productList);
              }}
              canFilter
              canExport
              canEdit={false}
              canAdd={false}
              csvFileName={`${DateHelper.ToDateString(new Date(), 'yyyy.MM.dd')}_Cancellation_Fees_Export`}
            />
          </Space>
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={() => {
              setModalId(null);
              setModalOpen(true);
            }}
            icon={<PlusOutlined />}
          >
            Add Cancellation Fee
          </Button>
        </Col>
      </Row>

      {showFilters && (
        <ScopeFilterRow
          variant="centre-school"
          useSpecificScope={scopeFilter.specificSchool}
          onUseSpecificScopeChange={(checked) =>
            updateScopeFilter(() => ({
              specificSchool: checked,
              centreId: null,
              schoolId: null,
            }))
          }
          primaryOptions={centreFilterOptions}
          secondaryOptions={schoolFilterOptions}
          primaryValue={centreFilterSelectValue}
          secondaryValue={schoolFilterSelectValue}
          onPrimaryChange={(value) =>
            updateScopeFilter((current) => ({
              ...current,
              centreId: value ?? null,
            }))
          }
          onSecondaryChange={(value) =>
            updateScopeFilter((current) => ({
              ...current,
              schoolId: value ?? null,
            }))
          }
          trailing={<FilterResetLink onClick={handleClearScopeFilter} />}
        />
      )}

      <Table
        columns={columns}
        dataSource={pagedData}
        rowKey="id"
        pagination={false}
        bordered
        size="middle"
        className="cancellation-fees-table"
        showSorterTooltip={false}
        onChange={(_, __, sorter: any, extra: any) => {
          handleSorterChange(sorter, extra?.action);
        }}
      />
      <TablePagination.Paging
        noOfItems={totalItems}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onItemsPerPageChange={(value, page) => {
          setItemsPerPage(value);
          setCurrentPage(page);
        }}
        onPageChange={page => setCurrentPage(page)}
        showItemsPerPage
      />

      <Modal
        title={modalId ? 'Update Cancellation Fee' : 'Add Cancellation Fee'}
        open={modalOpen}
        onCancel={() => {
          if (isFormSaving) return;
          setModalOpen(false);
          setModalId(null);
          setSaveStage('idle');
        }}
        footer={null}
        wrapClassName="cancellation-fees-modal"
        width={560}
        destroyOnHidden
        closable={!isFormSaving}
        mask={{ closable: !isFormSaving }}
        keyboard={!isFormSaving}
      >
        <AddEditCancellationFeeForm
          key={modalId ?? 'new'}
          editMode={modalId != null}
          cancellationFeeId={modalId ?? undefined}
          onSuccess={handleSaved}
          onSaveStageChange={setSaveStage}
        />
      </Modal>

      <Modal
        open={isFormSaving}
        title={null}
        footer={null}
        closable={false}
        mask={{ closable: false }}
        keyboard={false}
        centered
        width={400}
        zIndex={1100}
        rootClassName="cancellation-fees-saving-modal-root"
        className="cancellation-fees-saving-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(255, 255, 255, 0.18)',
          },
        }}
        aria-busy={saveStage !== 'completed'}
      >
        <div className="cancellation-fees-saving-modal-body">
          {saveStage === 'completed' ? (
            <CheckCircleOutlined className="cancellation-fees-saving-modal-success-icon" />
          ) : (
            <Spin size="large" />
          )}
          <Typography.Title level={5} className="cancellation-fees-saving-modal-title">
            {saveStage === 'completed' ? 'All done!' : 'Please wait...'}
          </Typography.Title>
          <CancellationFeeSaveProgress stage={saveStage} />
        </div>
      </Modal>

      <Modal
        title={assignedCentresSchoolsModalTitle}
        open={schoolListModalVisible}
        onCancel={() => setSchoolListModalVisible(false)}
        footer={null}
        width={420}
      >
        {schoolListDetails.length ? (
          <ul style={{ paddingLeft: 16 }}>
            {schoolListDetails.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        ) : (
          <p>No assigned centres or schools.</p>
        )}
      </Modal>
    </div>
  );
}

const mapStateToProps = (state: any): StateProps => ({
  cancellationFeeProducts: getCancellationFeesProductsData(state),
  filteredData: getCancellationFeesData(state),
});

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
  onSelectCancellationFeesTab: () =>
    dispatch(changeSelectedProductType('CancellationFees', false)),
  onListCancellationFees: (items) => dispatch(listCancellationFeesProductsData(items)),
  onFilterData: (filteredData) => dispatch(filterData(filteredData)),
  onSelectedData: item => dispatch(getCancellationFeeProductById(item!)),
  onLoadCancellationFees: (divisionId: number) => {
    return api.getAllCancellationFees(divisionId).then(data => {
      const mapped = Array.isArray(data) ? data.map(mapApiToGrid) : [];
      dispatch(listCancellationFeesProductsData(mapped));
    }).catch(error => {
      console.error('Failed to load cancellation fees', error);
    });
  },
  onCancel: () => {
    dispatch(setFormErrors([]));
    const divisionId = getSelectedDivisionId();
    return api.getAllCancellationFees(divisionId).then(data => {
      const mapped = Array.isArray(data) ? data.map(mapApiToGrid) : [];
      dispatch(resetCancellationFeeModal());
      dispatch(listCancellationFeesProductsData(mapped));
      return mapped;
    });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CancellationFeesGrid);
